import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Tabs, Tab } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SettingsSectionCard from './SettingsSectionCard';
import LotsInventorySettingsPanel from './LotsInventorySettingsPanel';
import CertifiedInventorySettingsPanel from './CertifiedInventorySettingsPanel';
import ShapesSettingsPanel from './ShapesSettingsPanel';
import { INVENTORY_SETTINGS_TABS, getInventorySettingsPath } from '../../utils/settingsForm';
import { resolveShapeOptions } from '../../utils/shapeOptions';

export default function InventorySettingsSection({
  form,
  setField,
  onSave,
  onReset,
  loading,
  saving,
  error,
  success,
  setSuccess,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [lotShapeOptions, setLotShapeOptions] = useState([]);
  const [certifiedShapeOptions, setCertifiedShapeOptions] = useState([]);

  const tabParam = searchParams.get('tab') || 'lots';
  const activeTab = INVENTORY_SETTINGS_TABS.includes(tabParam) ? tabParam : 'lots';
  const tabIndex = INVENTORY_SETTINGS_TABS.indexOf(activeTab);

  useEffect(() => {
    if (tabParam && !INVENTORY_SETTINGS_TABS.includes(tabParam)) {
      navigate(getInventorySettingsPath('lots'), { replace: true });
    }
  }, [tabParam, navigate]);

  useEffect(() => {
    axios.get('/api/diamonds/filters/options')
      .then((res) => {
        const customNames = (form.customShapes || []).map((s) => s.name).filter(Boolean);
        setLotShapeOptions(
          resolveShapeOptions(res.data?.lotShapes ?? res.data?.shapes, customNames),
        );
        setCertifiedShapeOptions(
          resolveShapeOptions(res.data?.certifiedShapes ?? res.data?.shapes, customNames),
        );
      })
      .catch(() => {
        setLotShapeOptions([]);
        setCertifiedShapeOptions([]);
      });
  }, [form.customShapes]);

  const sharedPanelProps = {
    form,
    setField,
    onSave,
    onReset,
    saving,
  };

  const renderPanel = () => {
    switch (activeTab) {
      case 'certified':
        return <CertifiedInventorySettingsPanel {...sharedPanelProps} shapeOptions={certifiedShapeOptions} />;
      case 'shapes':
        return <ShapesSettingsPanel {...sharedPanelProps} />;
      case 'lots':
      default:
        return <LotsInventorySettingsPanel {...sharedPanelProps} shapeOptions={lotShapeOptions} />;
    }
  };

  const handleTabChange = (_, newIndex) => {
    navigate(getInventorySettingsPath(INVENTORY_SETTINGS_TABS[newIndex]));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <SettingsSectionCard
      title={t('settings.sections.inventory')}
      description={t('settings.inventory.description')}
      error={error}
      success={success}
      successMessage={t('settings.saved')}
      onCloseSuccess={() => setSuccess(false)}
    >
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={t('settings.inventory.tabs.lots')} />
        <Tab label={t('settings.inventory.tabs.certified')} />
        <Tab label={t('settings.inventory.tabs.shapes')} />
      </Tabs>
      {renderPanel()}
    </SettingsSectionCard>
  );
}
